import moment from 'moment-timezone';

/*
 * Spot answers that probably shouldn't live forever, so the editor can offer a stop date.
 *
 *   "…runs every evening until October 31…"  -> { date: '2026-10-31', phrase: 'until October 31' }
 *   "…book before August 31…"                -> { date: next Aug 31 }
 *   "…until the end of November…"            -> { date: '2026-11-30' }
 *   "…our summer promo code…"                -> { date: null, phrase: 'promo code' }  (seasonal, no date)
 *
 * Pattern matching only, deliberately conservative: a wrong suggestion costs a click, a
 * missed one costs nothing new. The backend can do this better later in the same model
 * call that cleans up team answers.
 */
const MONTHS = [
  ['jan', 'january'], ['feb', 'february'], ['mar', 'march'], ['apr', 'april'], ['may', 'may'],
  ['jun', 'june'], ['jul', 'july'], ['aug', 'august'], ['sep', 'sept', 'september'],
  ['oct', 'october'], ['nov', 'november'], ['dec', 'december'],
];
const MONTH_RE = MONTHS.flat().sort((a, b) => b.length - a.length).join('|');
const monthIndex = (word) => MONTHS.findIndex((names) => names.includes(word.toLowerCase().replace('.', '')));

const LEAD = '(?:until|till|til|through|thru|before|by|ending|ends|ends on|up to|valid until|no later than)';
const ORD = '(?:st|nd|rd|th)?';
const YEAR = '(?:,?\\s+(\\d{4}))?';

const PATTERNS = [
  // "until the end of November"
  { re: new RegExp(`\\b${LEAD}\\s+(?:the\\s+)?end\\s+of\\s+(${MONTH_RE})\\.?${YEAR}\\b`, 'i'), parse: (m) => ({ month: m[1], day: 'end', year: m[2] }) },
  // "until October 31", "before Aug 31st, 2027"
  { re: new RegExp(`\\b${LEAD}\\s+(?:the\\s+)?(${MONTH_RE})\\.?\\s+(\\d{1,2})${ORD}${YEAR}\\b`, 'i'), parse: (m) => ({ month: m[1], day: m[2], year: m[3] }) },
  // "until 31 October", "by the 31st of October"
  { re: new RegExp(`\\b${LEAD}\\s+(?:the\\s+)?(\\d{1,2})${ORD}\\s+(?:of\\s+)?(${MONTH_RE})\\.?${YEAR}\\b`, 'i'), parse: (m) => ({ month: m[2], day: m[1], year: m[3] }) },
  // "until October" (whole month)
  { re: new RegExp(`\\b${LEAD}\\s+(${MONTH_RE})\\.?${YEAR}\\b(?!\\s+\\d)`, 'i'), parse: (m) => ({ month: m[1], day: 'end', year: m[2] }) },
];

const SEASONAL = /\b(?:this|the)\s+(?:summer|winter|spring|autumn|fall|season)\b|\bpromo(?:tion)?\s+code\b|\blimited[- ]time\b|\bseasonal\b|\bspecial offer\b/i;

function toDate({ month, day, year }, today) {
  const mi = monthIndex(month);
  if (mi < 0) return null;
  const build = (y) => {
    const base = moment({ year: y, month: mi, day: 1 });
    if (day === 'end') return base.endOf('month');
    const d = Number(day);
    return d >= 1 && d <= base.daysInMonth() ? base.date(d) : null;
  };
  if (year) return build(Number(year));
  // No year: the next time that date comes round (today counts).
  const thisYear = build(today.year());
  if (!thisYear) return null;
  return thisYear.isBefore(today, 'day') ? build(today.year() + 1) : thisYear;
}

export function detectExpiry(text, today = moment()) {
  if (!text) return null;
  for (const { re, parse } of PATTERNS) {
    const m = text.match(re);
    if (m) {
      const date = toDate(parse(m), today);
      if (date) return { date: date.format('YYYY-MM-DD'), phrase: m[0].trim() };
    }
  }
  const s = text.match(SEASONAL);
  return s ? { date: null, phrase: s[0].trim() } : null;
}

// Quick picks beside the date field.
export const quickStopDates = (today = moment()) => [
  { label: 'End of this month', date: today.clone().endOf('month').format('YYYY-MM-DD') },
  { label: 'In 3 months', date: today.clone().add(3, 'months').format('YYYY-MM-DD') },
  { label: 'End of the year', date: today.clone().endOf('year').format('YYYY-MM-DD') },
];
